import { useEffect, useId, useRef } from "react";
import { analytics } from "@/lib/site";

// The PHP pages embedded HubSpot by dropping a <script src="js.hsforms.net">
// plus an inline hbspt.forms.create() call straight into the markup, relying on
// the form landing in the script tag's parent node. That does not survive
// React rendering, so each embed now targets its own container.
//
// This is the site's only real contact form. send-data.php and the
// jQuery #contactForm handler in footer.php were dead code — the only markup
// with that id lived in index-1.php, which was not linked from the nav.

const EMBED_SRC = "https://js.hsforms.net/forms/embed/v2.js";

declare global {
  interface Window {
    hbspt?: {
      forms: { create: (opts: Record<string, unknown>) => void };
    };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadHubSpot(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.hbspt) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${EMBED_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject());
      return;
    }
    const s = document.createElement("script");
    s.src = EMBED_SRC;
    s.charset = "utf-8";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("HubSpot embed failed to load"));
    document.body.appendChild(s);
  });
  return scriptPromise;
}

export default function HubSpotForm({ className }: { className?: string }) {
  const reactId = useId();
  // useId produces colons, which are not valid in a CSS selector fragment.
  const targetId = `hs-form-${reactId.replace(/[^a-zA-Z0-9]/g, "")}`;
  const created = useRef(false);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (created.current) return;
    let cancelled = false;

    const load = () => loadHubSpot()
      .then(() => {
        if (cancelled || created.current || !window.hbspt) return;
        created.current = true;
        window.hbspt.forms.create({
          portalId: analytics.hubspot.portalId,
          formId: analytics.hubspot.formId,
          target: `#${targetId}`,
        });
      })
      .catch(() => {
        /* Network blocked or HubSpot down — the rest of the page is unaffected. */
      });

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer.disconnect();
        load();
      }
    }, { rootMargin: "800px" });
    if (container.current) observer.observe(container.current);

    return () => {
      observer.disconnect();
      cancelled = true;
    };
  }, [targetId]);

  return <div ref={container} id={targetId} className={className} style={{ minHeight: 600 }} />;
}
